import {
	type GraphIssue,
	NODE_CATEGORIES,
	NODE_TYPE_LABELS,
	type NodeType,
} from "@velizhanin-dev/bot-scenario"
import { ArrowRightLeft, Copy, MoreHorizontal, Play, Trash2, X } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import type { DraftNode, ScenarioDraft } from "@/entities/bot-builder"
import { CATEGORY_APPEARANCE, NODE_ICONS } from "@/entities/bot-builder"
import { cn } from "@/shared/lib/utils"
import { Button } from "@/shared/ui/button"
import { useConfirm } from "@/shared/ui/confirm-provider"
import { IssueText } from "@/shared/ui/form-field"
import { Popover, PopoverAnchor, PopoverContent } from "@/shared/ui/popover"
import { RowActionsMenu } from "@/shared/ui/row-actions-menu"
import { ScrollArea } from "@/shared/ui/scroll-area"
import { ToolButton } from "@/shared/ui/tool-button"
import type { DraftActions } from "../../model/use-scenario-draft"
import type { Config } from "./editor-props"
import { NODE_EDITORS } from "./node-editors"
import { NodeLinks } from "./node-links"

const AUTOSAVE_DELAY_MS = 600

interface NodeInspectorProps {
	draft: ScenarioDraft
	node: DraftNode
	issues: GraphIssue[]
	actions: DraftActions
	onClose: () => void
	onSelectNode: (nodeId: string) => void
	/** «Проиграть отсюда» в тест-чате. */
	onPlay: (nodeId: string) => void
}

/**
 * Панель блока. Правки копятся локально и сохраняются через паузу в наборе;
 * ответ сервера не перетирает то, что уже набрано. Родитель монтирует панель
 * заново для каждого блока (key), а при уходе с блока несохранённое уходит сразу.
 */
export function NodeInspector({
	draft,
	node,
	issues,
	actions,
	onClose,
	onSelectNode,
	onPlay,
}: NodeInspectorProps) {
	const { confirm } = useConfirm()
	const [config, setConfig] = useState<Config>(node.config ?? {})
	const [moveOpen, setMoveOpen] = useState(false)
	const [note, setNote] = useState(node.note ?? "")
	const pending = useRef<{ config?: Config; note?: string | null } | null>(null)
	const timer = useRef<number | undefined>(undefined)

	const flush = useCallback(() => {
		window.clearTimeout(timer.current)
		const patch = pending.current
		pending.current = null
		actions.markEditorPending(false)
		if (patch) void actions.updateNode(node.id, patch)
	}, [actions, node.id])

	useEffect(() => {
		const discard = () => {
			window.clearTimeout(timer.current)
			pending.current = null
			actions.markEditorPending(false)
		}
		window.addEventListener("kmk:discard-draft", discard)
		window.addEventListener("kmk:flush-draft", flush)
		return () => {
			window.removeEventListener("kmk:discard-draft", discard)
			window.removeEventListener("kmk:flush-draft", flush)
			flush()
		}
	}, [flush, actions])

	const schedule = (patch: { config?: Config; note?: string | null }) => {
		actions.markEditorPending(true)
		pending.current = { ...pending.current, ...patch }
		window.clearTimeout(timer.current)
		timer.current = window.setTimeout(flush, AUTOSAVE_DELAY_MS)
	}

	const type = node.type as NodeType
	const Editor = NODE_EDITORS[type]
	const Icon = NODE_ICONS[type]
	const appearance = CATEGORY_APPEARANCE[NODE_CATEGORIES[type] ?? "action"]
	const nodeLevelIssues = issues.filter((issue) => !issue.path)
	const errorCount = issues.filter((issue) => issue.severity === "error").length
	const otherFlows = draft.flows.filter((flow) => flow.id !== node.flowId)
	const flowName = draft.flows.find((flow) => flow.id === node.flowId)?.name
	const bodyRef = useRef<HTMLDivElement>(null)
	/** Прокрутить к первому полю с ошибкой и поставить в него фокус. */
	const showFirstIssue = () => {
		const target = bodyRef.current?.querySelector<HTMLElement>('[data-issue="error"]')
		if (!target) return
		target.scrollIntoView({ behavior: "smooth", block: "center" })
		const field = target.parentElement?.querySelector<HTMLElement>("input, textarea, select")
		field?.focus({ preventScroll: true })
	}

	return (
		<div className="flex h-full flex-col">
			<div className="flex items-start gap-3 border-b border-border/60 px-4 py-3.5">
				<span
					className={cn("grid size-9 shrink-0 place-items-center rounded-xl", appearance.tile)}
				>
					{Icon ? <Icon className="size-[18px]" /> : null}
				</span>
				<div className="min-w-0 flex-1">
					<p className="truncate text-[11px] leading-4 font-semibold tracking-[0.14em] text-primary uppercase">
						{NODE_TYPE_LABELS[type] ?? node.type}
						{flowName ? (
							<span className="font-normal tracking-normal text-muted-foreground normal-case">
								{" "}
								· {flowName}
							</span>
						) : null}
					</p>
					{type !== "start" ? (
						// Название блока прямо в заголовке: видно на схеме вместо типа.
						<input
							id="block-note"
							aria-label="Название блока"
							title="Название видно на схеме вместо типа блока"
							placeholder="Назвать блок"
							maxLength={500}
							value={note}
							onChange={(event) => {
								setNote(event.target.value)
								schedule({ note: event.target.value || null })
							}}
							className="mt-0.5 block w-full truncate rounded-md bg-transparent text-base leading-6 font-semibold tracking-tight outline-none transition-colors placeholder:text-muted-foreground/60 hover:bg-secondary/50 focus:bg-secondary/60 -mx-1 px-1"
						/>
					) : (
						<h2 className="mt-0.5 text-base leading-6 font-semibold tracking-tight">Старт</h2>
					)}
					{errorCount > 0 ? (
						<Button
							type="button"
							variant="link"
							onClick={showFirstIssue}
							className="h-auto p-0 text-xs text-red-400 underline-offset-2 hover:underline"
						>
							{errorCount} {errorCount === 1 ? "ошибка" : "ошибки"} · показать
						</Button>
					) : null}
				</div>
				<ToolButton label="Закрыть панель блока" className="size-9 -mt-0.5" onClick={onClose}>
					<X />
				</ToolButton>
			</div>

			<ScrollArea ref={bodyRef} className="flex-1">
				<div className="space-y-5 px-4 py-4">
					<IssueText issues={nodeLevelIssues} />
					{Editor ? (
						<Editor
							config={config}
							onChange={(next) => {
								setConfig(next)
								schedule({ config: next })
							}}
							issues={issues}
							draft={draft}
							node={node}
							scenarioId={draft.id}
						/>
					) : (
						<p className="text-sm text-muted-foreground">Неизвестный тип блока: {node.type}</p>
					)}
					<NodeLinks draft={draft} node={node} actions={actions} onSelectNode={onSelectNode} />
				</div>
			</ScrollArea>

			<div className="flex items-center gap-2 border-t border-border/60 px-3 py-2.5">
				<Button
					variant="secondary"
					size="sm"
					className="h-9 flex-1 rounded-xl"
					title="Запустить тест-чат с этого блока"
					onClick={() => {
						flush()
						onPlay(node.id)
					}}
				>
					<Play />
					Проиграть отсюда
				</Button>
				{type !== "start" ? (
					<Popover open={moveOpen} onOpenChange={setMoveOpen}>
					<PopoverAnchor asChild>
					<div>
					<RowActionsMenu
						label="Действия с блоком"
						align="end"
						contentClassName="w-64"
						actions={[
							{
								label: "Копия блока",
								icon: Copy,
								onSelect: async () => {
									flush()
									const result = await actions.copyNode(node.id)
									if (result) onSelectNode(result.node.id)
								},
							},
							{
								label: "Перенести в другую воронку",
								icon: ArrowRightLeft,
								hidden: otherFlows.length === 0,
								onSelect: () => setMoveOpen(true),
							},
							{
								label: "Удалить блок",
								icon: Trash2,
								destructive: true,
								separatorBefore: true,
								onSelect: () =>
									confirm({
										title: "Удалить блок?",
										message: "Связи блока тоже удалятся.",
										confirmText: "Удалить",
										onConfirm: async () => {
											window.clearTimeout(timer.current)
											pending.current = null
											await actions.deleteNode(node.id)
											onClose()
										},
									}),
							},
						]}
						trigger={
							<Button variant="ghost" size="icon" className="size-9 rounded-xl" aria-label="Действия с блоком">
								<MoreHorizontal />
							</Button>
						}
					/>
					</div>
					</PopoverAnchor>
					<PopoverContent side="top" align="end" className="max-h-72 w-60 overflow-y-auto rounded-xl p-1">
						<p className="px-2.5 pt-1.5 pb-1 text-xs text-muted-foreground">Перенести в воронку</p>
						{otherFlows.map((flow) => (
							<Button
								key={flow.id}
								type="button"
								variant="ghost"
								className="h-auto w-full justify-start truncate rounded-lg px-2.5 py-2 text-left text-sm"
								onClick={async () => {
									setMoveOpen(false)
									flush()
									const result = await actions.moveNode(node.id, flow.id)
									if (result) onSelectNode(result.node.id)
								}}
							>
								{flow.name}
							</Button>
						))}
					</PopoverContent>
					</Popover>
				) : null}
			</div>
		</div>
	)
}
